import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, FloatButton, Space, Typography } from 'antd';
import {
  CloseOutlined,
  CommentOutlined,
  CompressOutlined,
  ExpandOutlined,
  LoginOutlined,
  MessageOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import TeamAssistantChat from './TeamAssistantChat';
import { canAccessApp } from '../../utils/roles';
import './team-assistant.css';

const { Text, Title } = Typography;

/**
 * @param {'inline' | 'drawer' | 'card' | 'floating'} mode
 * @param {'default' | 'mini'} size — mini = compact chat-assistant style
 */
const CHATGPT_THEME_CLASS = 'team-assistant-root--chatgpt-theme';
const WIDGET_CHATGPT_CLASS = 'team-assistant-widget--chatgpt';

const mergeChatClassName = (extra = '') =>
  [CHATGPT_THEME_CLASS, extra].filter(Boolean).join(' ');

const TeamAssistantWidget = ({
  mode = 'inline',
  size = 'default',
  currentUser,
  title = 'AI assistant',
  subtitle,
  variant = 'embedded',
  drawerWidth = 420,
  floatButtonLabel = 'Team AI',
  loginPath = '/login',
  loginHint = 'Sign in as admin or staff to use the shared team assistant.',
  className = '',
  features,
  messagesMaxHeight,
  ...chatProps
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFullscreen, setDrawerFullscreen] = useState(false);
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [floatingFullscreen, setFloatingFullscreen] = useState(false);
  const canUse = currentUser && canAccessApp(currentUser);

  const closeFloating = () => {
    setFloatingOpen(false);
    setFloatingFullscreen(false);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerFullscreen(false);
  };

  useEffect(() => {
    if (!floatingFullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [floatingFullscreen]);

  const fullscreenToggleButton = (fullscreen, setFullscreen) => (
    <Button
      type="text"
      size="small"
      icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
      aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
      onClick={() => setFullscreen((v) => !v)}
    />
  );

  const floatingPanelHeadActions = (onClose, fullscreen, setFullscreen) => (
    <Space size={0}>
      {fullscreenToggleButton(fullscreen, setFullscreen)}
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined />}
        aria-label="Close"
        onClick={onClose}
      />
    </Space>
  );

  const isMini = size === 'mini';

  const defaultMiniMessagesHeight = 'min(calc(90vh - 200px), 560px)';

  const chatPropsResolved = useMemo(
    () => ({
      ...chatProps,
      variant: isMini ? 'compact' : variant,
      title,
      subtitle: isMini ? undefined : subtitle,
      className: mergeChatClassName(chatProps.className),
      messagesMaxHeight: messagesMaxHeight ?? (isMini ? defaultMiniMessagesHeight : undefined),
      transcriptDefaultOpen: false,
      features: isMini
        ? {
            footnote: false,
            catalogTags: false,
            voice: true,
            voiceChatMode: false,
            webSearch: true,
            singleLineComposer: true,
            ...chatProps.features,
            ...features,
          }
        : { singleLineComposer: true, footnote: false, ...chatProps.features, ...features },
    }),
    [isMini, variant, title, subtitle, messagesMaxHeight, features, chatProps]
  );

  const floatingChatSize = floatingFullscreen ? 'default' : 'mini';
  const floatingChatProps = useMemo(
    () => ({
      ...chatPropsResolved,
      className: mergeChatClassName(chatPropsResolved.className),
      variant: floatingFullscreen ? 'embedded' : 'compact',
      subtitle: floatingFullscreen ? subtitle : undefined,
      messagesMaxHeight: floatingFullscreen
        ? undefined
        : defaultMiniMessagesHeight,
      transcriptDefaultOpen: floatingFullscreen,
      features: floatingFullscreen
        ? { footnote: true, catalogTags: true, voice: true, singleLineComposer: true, ...features }
        : chatPropsResolved.features,
    }),
    [chatPropsResolved, floatingFullscreen, subtitle, features]
  );

  if (!canUse) {
    if (mode === 'drawer') return null;
    if (mode === 'floating') {
      return (
        <div className={`team-assistant-floating-host ${WIDGET_CHATGPT_CLASS} ${className}`}>
          {!floatingOpen && (
            <button
              type="button"
              className="team-assistant-launcher"
              onClick={() => setFloatingOpen(true)}
              aria-label="Open AI assistant"
            >
              <RobotOutlined className="team-assistant-launcher-icon" />
              <span className="team-assistant-launcher-text">{title}</span>
            </button>
          )}
          {floatingOpen && (
            <div className="team-assistant-floating-panel team-assistant-floating-panel--guest" role="dialog">
              <div className="team-assistant-floating-panel-head">
                <span className="team-assistant-floating-panel-title">
                  <RobotOutlined /> {title}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  aria-label="Close"
                  onClick={() => setFloatingOpen(false)}
                />
              </div>
              <div className="team-assistant-floating-panel-body">
                <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                  {loginHint}
                </Text>
                <Link to={loginPath}>
                  <Button type="primary" block icon={<LoginOutlined />}>Sign in</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <Card className={`team-assistant-guest-card ${className}`} size="small">
        <Title level={5} style={{ marginTop: 0, fontSize: 15 }}>
          <MessageOutlined /> {title}
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>{loginHint}</Text>
        <div style={{ marginTop: 12 }}>
          <Link to={loginPath}>
            <Button type="primary" size="small" icon={<LoginOutlined />}>Sign in</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const chatElement = (
    <TeamAssistantChat
      currentUser={currentUser}
      size={size}
      showHeader={!isMini && chatProps.showHeader !== false}
      {...chatPropsResolved}
    />
  );

  if (mode === 'floating') {
    const hostClass = [
      'team-assistant-floating-host',
      WIDGET_CHATGPT_CLASS,
      floatingFullscreen ? 'team-assistant-floating-host--fullscreen' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const panelClass = [
      'team-assistant-floating-panel',
      floatingFullscreen ? 'team-assistant-floating-panel--fullscreen' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={hostClass}>
        {!floatingOpen && (
          <button
            type="button"
            className="team-assistant-launcher"
            onClick={() => setFloatingOpen(true)}
            aria-label="Open AI assistant"
          >
            <RobotOutlined className="team-assistant-launcher-icon" />
            <span className="team-assistant-launcher-text">{title}</span>
          </button>
        )}
        {floatingOpen && (
          <div className={panelClass} role="dialog" aria-label={title}>
            <div className="team-assistant-floating-panel-head">
              <span className="team-assistant-floating-panel-title">
                <RobotOutlined /> {title}
              </span>
              {floatingPanelHeadActions(closeFloating, floatingFullscreen, setFloatingFullscreen)}
            </div>
            <div className="team-assistant-floating-panel-body">
              <TeamAssistantChat
                currentUser={currentUser}
                size={floatingChatSize}
                showHeader={floatingFullscreen}
                scrollResetKey={floatingOpen ? (floatingFullscreen ? 'float-fs' : 'float') : 'closed'}
                {...floatingChatProps}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (mode === 'drawer') {
    return (
      <>
        <FloatButton
          type="primary"
          icon={<CommentOutlined />}
          tooltip={floatButtonLabel}
          onClick={() => setDrawerOpen(true)}
          className="team-assistant-float-btn"
        />
        <Drawer
          title={title}
          placement="right"
          width={drawerFullscreen ? '100%' : isMini ? 400 : drawerWidth}
          open={drawerOpen}
          onClose={closeDrawer}
          className={`team-assistant-drawer team-assistant-widget--chatgpt${drawerFullscreen ? ' team-assistant-drawer--fullscreen' : ''}`}
          destroyOnClose={false}
          extra={fullscreenToggleButton(drawerFullscreen, setDrawerFullscreen)}
        >
          <TeamAssistantChat
            currentUser={currentUser}
            size={drawerFullscreen ? 'default' : isMini ? 'mini' : size}
            showHeader={drawerFullscreen}
            scrollResetKey={drawerOpen ? 'open' : 'closed'}
            {...(drawerFullscreen
              ? {
                  ...chatPropsResolved,
                  variant: 'embedded',
                  subtitle,
                  className: mergeChatClassName(chatProps.className),
                  messagesMaxHeight: undefined,
                  transcriptDefaultOpen: true,
                  features: {
                    footnote: true,
                    catalogTags: true,
                    voice: true,
                    singleLineComposer: true,
                    ...features,
                  },
                }
              : chatPropsResolved)}
          />
        </Drawer>
      </>
    );
  }

  if (mode === 'card') {
    return (
      <Card
        className={`team-assistant-widget-card team-assistant-widget--chatgpt ${isMini ? 'team-assistant-widget-card--mini' : ''} ${className}`}
        size={isMini ? 'small' : 'default'}
        bordered
      >
        {chatElement}
      </Card>
    );
  }

  return (
    <section
      className={`team-assistant-widget-inline team-assistant-widget--chatgpt ${isMini ? 'team-assistant-widget-inline--mini' : ''} ${className}`}
    >
      {chatElement}
    </section>
  );
};

export default TeamAssistantWidget;
